import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Download, Eye, FileText, X } from 'lucide-react'
import { recruitmentAdminApi } from '../../../services/api'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import { formatDate } from '../../../utils/date'
import { useMarkNotificationsRead } from '../../../hooks/useMarkNotificationsRead'
import { useLanguage } from '../../../context/LanguageContext'

export default function AdminJobApplications() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  useMarkNotificationsRead('recruitment_applications')
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [page, setPage] = useState(1)
  const [stats, setStats] = useState(null)
  const [departments, setDepartments] = useState([])
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [specializationId, setSpecializationId] = useState('')
  const [cvPreview, setCvPreview] = useState(null)

  const selectedDepartment = departments.find((dept) => String(dept.id) === String(departmentId))
  const availableSpecializations = selectedDepartment?.specializations || []

  const load = (pageNum = page) => {
    setLoading(true)
    Promise.all([
      recruitmentAdminApi.applications.getAll({
        page: pageNum,
        per_page: 20,
        search: search || undefined,
        status: status || undefined,
        department_id: departmentId || undefined,
        specialization_id: specializationId || undefined,
      }),
      recruitmentAdminApi.applications.getStats(),
      recruitmentAdminApi.departments.getAll(),
      recruitmentAdminApi.statuses.getAll(),
    ])
      .then(([appsRes, statsRes, deptRes, statusRes]) => {
        setItems(appsRes.data.data || appsRes.data || [])
        setMeta(appsRes.data.meta || null)
        setStats(statsRes.data.data || statsRes.data)
        setDepartments(deptRes.data.data || deptRes.data || [])
        setStatuses(statusRes.data.data || statusRes.data || [])
      })
      .catch(() => {
        setItems([])
        setMeta(null)
        setStats(null)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(page)
  }, [page, search, status, departmentId, specializationId])

  const resetAndFilter = (updater) => {
    setPage(1)
    updater()
  }

  const departmentName = (item) => {
    const dept = item.department
    if (!dept) return '—'
    return language === 'ar' ? dept.name_ar || dept.name_en : dept.name_en || dept.name_ar
  }

  const specializationName = (item) => {
    const spec = item.specialization
    if (!spec) return '—'
    return language === 'ar' ? spec.name_ar || spec.name_en : spec.name_en || spec.name_ar
  }

  const handleExport = async () => {
    const { data } = await recruitmentAdminApi.applications.exportExcel({
      status: status || undefined,
      department_id: departmentId || undefined,
      specialization_id: specializationId || undefined,
    })
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.download = 'job-applications.xls'
    link.click()
  }

  const handleViewCv = async (item) => {
    const attachment = item.cv_attachment || item.attachments?.find((file) => file.type === 'cv')
    if (!attachment) return

    const { data } = await recruitmentAdminApi.applications.viewAttachment(item.id, attachment.id)
    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
    setCvPreview({ url, name: attachment.original_name, applicant: item.full_name })
  }

  const closeCvPreview = () => {
    if (cvPreview?.url) window.URL.revokeObjectURL(cvPreview.url)
    setCvPreview(null)
  }

  return (
    <div>
      {stats ? (
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="p-5">
            <p className="text-sm text-text/60">{t('admin.recruitment.statsTotal')}</p>
            <p className="mt-1 text-3xl font-bold text-primary">{stats.total}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm text-text/60">{t('admin.recruitment.statsNew')}</p>
            <p className="mt-1 text-3xl font-bold text-accent">{stats.new}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm text-text/60">{t('admin.recruitment.statsThisMonth')}</p>
            <p className="mt-1 text-3xl font-bold text-primary">{stats.this_month}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm text-text/60">{t('admin.recruitment.statsDepartments')}</p>
            <p className="mt-1 text-3xl font-bold text-primary">{stats.by_department?.length || 0}</p>
          </Card>
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => {
            const value = e.target.value
            resetAndFilter(() => setSearch(value))
          }}
          placeholder={t('admin.recruitment.searchApplications')}
          className="rounded-xl border border-border px-4 py-2 text-sm"
        />
        <select
          value={departmentId}
          onChange={(e) => {
            const value = e.target.value
            resetAndFilter(() => {
              setDepartmentId(value)
              setSpecializationId('')
            })
          }}
          className="rounded-xl border border-border px-4 py-2 text-sm"
        >
          <option value="">{t('admin.recruitment.allDepartments')}</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {language === 'ar' ? dept.name_ar : dept.name_en}
            </option>
          ))}
        </select>
        {availableSpecializations.length > 0 ? (
          <select
            value={specializationId}
            onChange={(e) => {
              const value = e.target.value
              resetAndFilter(() => setSpecializationId(value))
            }}
            className="rounded-xl border border-border px-4 py-2 text-sm"
          >
            <option value="">{t('admin.recruitment.allSpecializations')}</option>
            {availableSpecializations.map((spec) => (
              <option key={spec.id} value={spec.id}>
                {language === 'ar' ? spec.name_ar : spec.name_en}
              </option>
            ))}
          </select>
        ) : null}
        <select
          value={status}
          onChange={(e) => {
            const value = e.target.value
            resetAndFilter(() => setStatus(value))
          }}
          className="rounded-xl border border-border px-4 py-2 text-sm"
        >
          <option value="">{t('admin.recruitment.allStatuses')}</option>
          {statuses.map((s) => (
            <option key={s.id} value={s.key}>{s.name_en}</option>
          ))}
        </select>
        <Button variant="outlineDark" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4" />
          {t('admin.recruitment.exportExcel')}
        </Button>
      </div>

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <>
          <Card padding={false} className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-border bg-primary/5">
                <tr className="text-start">
                  <th className="px-4 py-3">{t('admin.recruitment.reference')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.applicant')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.department')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.specialization')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.email')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.phone')}</th>
                  <th className="px-4 py-3">{t('admin.recruitment.submitted')}</th>
                  <th className="px-4 py-3">{t('admin.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/60">
                    <td className="px-4 py-3 font-medium">{item.reference_number}</td>
                    <td className="px-4 py-3">{item.full_name}</td>
                    <td className="px-4 py-3">{departmentName(item)}</td>
                    <td className="px-4 py-3">{specializationName(item)}</td>
                    <td className="px-4 py-3">{item.email}</td>
                    <td className="px-4 py-3">{item.phone}</td>
                    <td className="px-4 py-3">{formatDate(item.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {(item.cv_attachment || item.attachments?.some((file) => file.type === 'cv')) ? (
                          <button
                            type="button"
                            onClick={() => handleViewCv(item)}
                            className="rounded-lg p-2 text-accent transition-colors hover:bg-accent/10"
                            aria-label={t('admin.recruitment.viewCv')}
                            title={t('admin.recruitment.viewCv')}
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                        ) : null}
                        <Link
                          to={`/cp-login/recruitment/applications/${item.id}`}
                          className="rounded-lg p-2 text-primary transition-colors hover:bg-primary/10"
                          aria-label={t('admin.recruitment.view')}
                          title={t('admin.recruitment.view')}
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {meta && meta.last_page > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outlineDark" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('admin.pagination.prev')}
              </Button>
              <span className="text-sm text-text/60">
                {page} / {meta.last_page}
                {meta.total ? ` · ${meta.total}` : ''}
              </span>
              <Button
                variant="outlineDark"
                size="sm"
                disabled={page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                {t('admin.pagination.next')}
              </Button>
            </div>
          ) : null}
        </>
      )}

      {cvPreview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <p className="font-semibold text-primary">{cvPreview.applicant}</p>
                <p className="text-sm text-text/60">{cvPreview.name}</p>
              </div>
              <button type="button" onClick={closeCvPreview} className="rounded-lg p-2 hover:bg-primary/5">
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe title={cvPreview.name} src={cvPreview.url} className="h-full w-full" />
          </div>
        </div>
      ) : null}
    </div>
  )
}
